import { useState } from 'react';
import { toast } from 'react-toastify';
import type { Plan } from '@t/productOffering.types';
import { useAddChildItem } from '@hooks/useAddChildItem/useAddChildItem';
import { useDeleteChildItems } from '@hooks/useDeleteChildItem/useDeleteChildItem';
import { useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useCart } from '@hooks/useCart/useCart';

type BundleRelationshipFlowParams = {
  sessionId: string;
  /** Plan from the mutation result — non-null when the mutation resolved with hasBundleRelationship. */
  mutationBundlePlan: Plan | null;
  /** Called after a successful confirm in new-plan mode to reset mutation state. */
  onAfterNewConfirm: () => void;
};

type BundleRelationshipFlowReturn = {
  openBundleForEdit: (plan: Plan) => void;
  /** Non-null when the bundle dialog should be open (edit or new mode). */
  bundleSelectionPlan: Plan | null;
  /** Pre-selected offering IDs injected into the dialog when editing. */
  bundleEditInitialIds: string[];
  handleBundleRelationshipConfirmed: (mainPlan: Plan, selectedOfferings: Plan[]) => Promise<void>;
  handleBundleRelationshipDismissed: () => void;
  isBundleConfirming: boolean;
};

export const useBundleRelationshipFlow = ({
  sessionId,
  mutationBundlePlan,
  onAfterNewConfirm,
}: BundleRelationshipFlowParams): BundleRelationshipFlowReturn => {
  const { selectPlanAsync } = useShoppingCart(sessionId);
  const { data: cart } = useCart(sessionId);
  const addChildItemM = useAddChildItem(sessionId);
  const deleteChildItemsM = useDeleteChildItems(sessionId);

  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [editInitialIds, setEditInitialIds] = useState<string[]>([]);

  const openBundleForEdit = (plan: Plan): void => {
    const cartItem = cart?.cartItem?.find((item) => item.productOffering?.id === plan.planId);
    const existingChildOfferingIds = (cartItem?.childItems ?? [])
      .map((c) => c.productOffering?.id)
      .filter((id): id is string => !!id);
    setEditInitialIds(existingChildOfferingIds);
    setEditingPlan(plan);
  };

  const resetEditState = () => {
    setEditingPlan(null);
    setEditInitialIds([]);
  };

  const handleBundleRelationshipConfirmed = async (
    mainPlan: Plan,
    selectedOfferings: Plan[]
  ): Promise<void> => {
    try {
      if (editingPlan) {
        const cartItem = cart?.cartItem?.find((item) => item.productOffering?.id === mainPlan.planId);
        const existingChildItemIds = (cartItem?.childItems ?? [])
          .map((c) => c.id)
          .filter((id): id is string => !!id);
        if (existingChildItemIds.length > 0) {
          await deleteChildItemsM.mutateAsync(existingChildItemIds);
        }
        if (selectedOfferings.length > 0) {
          await addChildItemM.mutateAsync({
            parentOfferingCode: mainPlan.planId,
            items: selectedOfferings.map((o) => ({
              action: 'add',
              quantity: 1,
              productOffering: { id: o.planId, name: o.name },
            })),
          });
        }
        resetEditState();
      } else {
        await selectPlanAsync(mainPlan);
        if (selectedOfferings.length > 0) {
          await addChildItemM.mutateAsync({
            parentOfferingCode: mainPlan.planId,
            items: selectedOfferings.map((o) => ({
              action: 'add',
              quantity: 1,
              productOffering: { id: o.planId, name: o.name },
            })),
          });
        }
        onAfterNewConfirm();
      }
    } catch {
      toast.error('Something went wrong while adding the plan to your cart. Please try again.');
    }
  };

  const handleBundleRelationshipDismissed = (): void => {
    resetEditState();
  };

  return {
    openBundleForEdit,
    bundleSelectionPlan: editingPlan ?? mutationBundlePlan,
    bundleEditInitialIds: editInitialIds,
    handleBundleRelationshipConfirmed,
    handleBundleRelationshipDismissed,
    isBundleConfirming: addChildItemM.isPending || deleteChildItemsM.isPending,
  };
};
